/**
 * Matrice voci × offerte di una comparazione.
 *
 * La stessa per il calcolo dal vivo e per uno snapshot salvato: riceve la forma che produce
 * `comparazioneGara` (backend) e la mostra senza ricalcolare niente. Le colonne sono in ordine di
 * ranking, le escluse in fondo; una cella rossa è una voce non conforme.
 */

import React from "react";
import { SEZIONE_VOCE } from "../constants/optionSet";
import { etichetta } from "../utils/formato";

const num = (n) =>
  n == null || n === ""
    ? "—"
    : typeof n === "number"
      ? n.toLocaleString("it-IT", { maximumFractionDigits: 2 })
      : String(n);

const CLASSE_CONFORMITA = {
  non_conforme: "bg-red-50 text-red-800",
  da_valutare: "bg-yellow-50",
};

export default function MatriceComparazione({ comparazione, evidenzia }) {
  if (!comparazione) return null;
  const offerte = [...comparazione.offerte].sort(
    (a, b) => (a.posizione ?? 999) - (b.posizione ?? 999),
  );
  if (!offerte.length) {
    return (
      <p className="text-sm text-gray-500 p-4">Nessuna offerta da comparare.</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th className="min-w-[14rem]">Voce</th>
            {offerte.map((o) => (
              <th
                key={o.offertaId}
                className={`text-center min-w-[9rem] ${evidenzia && String(evidenzia).toLowerCase() === String(o.offertaId).toLowerCase() ? "bg-acquisti-50" : ""}`}
              >
                <div className="normal-case text-gray-900">{o.fornitore}</div>
                <div className="text-[10px] normal-case font-normal text-gray-500">
                  rev. {o.revisione ?? "?"}
                  {o.uscente ? " · uscente" : ""}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-sm">
          {comparazione.voci.map((v) => (
            <tr key={v.id}>
              <td>
                <div className="font-medium">
                  {v.nome}
                  {v.obbligatorio && (
                    <span className="badge-danger ml-1">obbl.</span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {etichetta(SEZIONE_VOCE, v.sezione, "")} · peso {v.peso} · max{" "}
                  {v.punteggioMassimo}
                </div>
              </td>
              {offerte.map((o) => {
                const e = o.voci.find((x) => x.voceId === v.id) || {};
                return (
                  <td
                    key={o.offertaId}
                    className={`text-center ${CLASSE_CONFORMITA[e.conformita] || ""}`}
                  >
                    <div className="text-xs text-gray-600">
                      {num(e.valoreUsato)}
                      {v.unitaMisura && e.valoreUsato != null
                        ? ` ${v.unitaMisura}`
                        : ""}
                    </div>
                    <div className="font-semibold">{num(e.punteggio)}</div>
                    {e.fonte === "valutazione" && (
                      <div className="text-[10px] text-acquisti-700">
                        da valutazione fornitore
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="bg-gray-50">
            <td className="font-semibold">Totale su 100</td>
            {offerte.map((o) => (
              <td key={o.offertaId} className="text-center">
                {o.esclusa ? (
                  <span className="badge-danger">esclusa</span>
                ) : (
                  <>
                    <div className="text-lg font-bold">{num(o.totale)}</div>
                    <div className="text-xs text-gray-500">#{o.posizione}</div>
                  </>
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      {offerte.some((o) => o.esclusa || o.avvisi?.length) && (
        <ul className="text-sm p-4 space-y-1">
          {offerte
            .filter((o) => o.esclusa)
            .map((o) => (
              <li key={`e-${o.offertaId}`} className="text-red-800">
                <strong>{o.fornitore}</strong> esclusa — {o.motivoEsclusione}
              </li>
            ))}
          {offerte
            .filter((o) => o.avvisi?.length)
            .map((o) => (
              <li key={`a-${o.offertaId}`} className="text-yellow-800">
                <strong>{o.fornitore}</strong>: {o.avvisi.join("; ")} — valutala
                a mano nell'offerta («Conformità»), o resterà ammessa.
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
